/**
 * Client helpers for the tenant navigation policy. Used by the
 * NavPreferencesProvider, the admin policy editor, and the post-login landing
 * resolver. All failures degrade to the permissive default — a flaky policy
 * fetch must never lock a user out of their navigation.
 */
import {
  coerceNavigationPolicy,
  DEFAULT_NAVIGATION_POLICY,
  type NavigationPolicy,
} from "./policy";
import {
  coerceNavPresence,
  DEFAULT_NAV_PRESENCE,
  type NavPresence,
} from "./presence";

const ENDPOINT = "/api/settings/navigation-policy";

export type NavigationArrangement = {
  policy: NavigationPolicy;
  /** Which work surfaces this workspace has produced (grows the starter rail). */
  presence: NavPresence;
};

/**
 * GET the effective policy + work presence for the current tenant; defaults on
 * any failure. Both defaults are the permissive ones — a flaky fetch must
 * never lock a user out of their navigation, nor take a row away from them.
 */
export async function fetchNavigationArrangement(): Promise<NavigationArrangement> {
  try {
    const res = await fetch(ENDPOINT, { cache: "no-store" });
    if (!res.ok) {
      return { policy: { ...DEFAULT_NAVIGATION_POLICY }, presence: { ...DEFAULT_NAV_PRESENCE } };
    }
    const data = await res.json();
    return {
      policy: coerceNavigationPolicy(data?.policy ?? data),
      presence: coerceNavPresence(data?.presence),
    };
  } catch {
    return { policy: { ...DEFAULT_NAVIGATION_POLICY }, presence: { ...DEFAULT_NAV_PRESENCE } };
  }
}

/** Policy only — for callers (the admin editor) that don't arrange a rail. */
export async function fetchNavigationPolicy(): Promise<NavigationPolicy> {
  return (await fetchNavigationArrangement()).policy;
}

/** PUT a new policy (admin only). Throws on a non-OK response. */
export async function saveNavigationPolicy(policy: NavigationPolicy): Promise<NavigationPolicy> {
  const res = await fetch(ENDPOINT, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(coerceNavigationPolicy(policy)),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.error ?? `HTTP ${res.status}`);
  }
  const data = await res.json();
  return coerceNavigationPolicy(data?.policy ?? data);
}
